import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface VersionUserItem {
  id: string;
  name: string;
  email: string;
  factoryName: string;
  appVersion: string;
  androidVersion: string;
  isOutdated: boolean;
}

// Parse a version string ("v1.4.2", "1.4.2", "1.4.2+build") into comparable numbers.
const parseVersion = (raw?: string): number[] | null => {
  if (!raw) return null;
  const cleaned = raw.trim().toLowerCase().replace(/^v/, '').split(/[+\-\s]/)[0];
  const parts = cleaned.split('.').map(p => parseInt(p, 10));
  if (parts.length === 0 || parts.some(n => isNaN(n))) return null;
  return parts;
};

// Returns > 0 if a is newer than b, < 0 if older, 0 if equal/uncomparable.
const compareVersions = (a?: string, b?: string): number => {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  if (!pa || !pb) return 0;
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

export const VersionLogPanel: React.FC = () => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

  const [selectedFactoryFilter, setSelectedFactoryFilter] = useState("All Factories");
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState<VersionUserItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  // Fetch every organization, then each org's users, and flatten into the
  // version log. App + Android version come straight from the device sync data.
  const fetchVersionLog = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = { ...(token && { Authorization: `Bearer ${token}` }) };

      const orgRes = await fetch(`${baseUrl}/api/v1/organizations`, { headers });
      const orgData = await orgRes.json();
      if (!orgRes.ok || !orgData.success) {
        throw new Error(orgData.message || 'Failed to fetch organizations');
      }
      const orgs: any[] = orgData.data || [];

      const perOrgUsers = await Promise.all(orgs.map(async (org) => {
        try {
          const res = await fetch(`${baseUrl}/api/v1/organizations/${org.id}/users`, { headers });
          const data = await res.json();
          if (!res.ok || !data.success) return [];
          return (data.data || []).map((u: any): VersionUserItem => ({
            id: String(u.id),
            name: u.name || '—',
            email: u.email || '—',
            factoryName: org.name,
            appVersion: u.app_version || '',
            androidVersion: u.os_version || '',
            isOutdated: false, // computed below once the latest version is known
          }));
        } catch {
          return [];
        }
      }));

      const flat = perOrgUsers.flat();

      // The "latest" build is the highest app version actually reported across
      // all devices. Anything reporting a known, older version is outdated.
      let latest = '';
      for (const u of flat) {
        if (u.appVersion && compareVersions(u.appVersion, latest) > 0) latest = u.appVersion;
      }
      const withFlags = flat.map(u => ({
        ...u,
        isOutdated: !!u.appVersion && !!latest && compareVersions(u.appVersion, latest) < 0,
      }));

      setUsers(withFlags);
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load version data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchVersionLog(); }, []);

  // Latest (golden) app version across all reporting devices.
  const latestVersion = useMemo(() => {
    let latest = '';
    for (const u of users) {
      if (u.appVersion && compareVersions(u.appVersion, latest) > 0) latest = u.appVersion;
    }
    return latest;
  }, [users]);

  // Factory list is derived from the live data (plus the "All Factories" entry).
  const FACTORIES = useMemo(() => {
    const names = Array.from(new Set(users.map(u => u.factoryName))).sort((a, b) => a.localeCompare(b));
    return ["All Factories", ...names];
  }, [users]);

  // Compute summary stats dynamically for each factory selection
  const factorySummary = useMemo(() => {
    return FACTORIES.map(fac => {
      const facUsers = fac === "All Factories" ? users : users.filter(u => u.factoryName === fac);
      const total = facUsers.length;
      const outdated = facUsers.filter(u => u.isOutdated).length;
      return {
        name: fac,
        total,
        outdated
      };
    });
  }, [users, FACTORIES]);

  // Filter & sort logic: Outdated versions MUST be listed on top
  const filteredUsers = useMemo(() => {
    let result = [...users];

    // Factory filter
    if (selectedFactoryFilter !== "All Factories") {
      result = result.filter(u => u.factoryName === selectedFactoryFilter);
    }

    // Search term filter
    if (searchTerm.trim() !== "") {
      const query = searchTerm.toLowerCase();
      result = result.filter(u => 
        u.name.toLowerCase().includes(query) || 
        u.email.toLowerCase().includes(query) ||
        u.appVersion.toLowerCase().includes(query) ||
        u.androidVersion.toLowerCase().includes(query)
      );
    }

    // Sort outdated versions on top (isOutdated is true first)
    result.sort((a, b) => {
      if (a.isOutdated && !b.isOutdated) return -1;
      if (!a.isOutdated && b.isOutdated) return 1;
      // Secondary sort by name
      return a.name.localeCompare(b.name);
    });

    return result;
  }, [users, selectedFactoryFilter, searchTerm]);

  // Aggregate stats
  const stats = useMemo(() => {
    // We compute stats based on the selected factory filter ONLY
    const relevantUsers = selectedFactoryFilter === "All Factories" 
      ? users 
      : users.filter(u => u.factoryName === selectedFactoryFilter);

    const total = relevantUsers.length;
    const outdated = relevantUsers.filter(u => u.isOutdated).length;
    const updated = total - outdated;

    return {
      total,
      outdated,
      updated,
      percentageOutdated: total > 0 ? Math.round((outdated / total) * 100) : 0
    };
  }, [users, selectedFactoryFilter]);

  const handleTriggerMockOTA = (userName: string) => {
    setAlertMessage(`OTA device patch command successfully queued for agent ${userName}. Handshake pending...`);
    setTimeout(() => {
      setAlertMessage(null);
    }, 5000);
  };

  const handleUpdateAllOutdated = () => {
    const goldenLabel = latestVersion || 'the latest build';
    setAlertMessage(`OTA update command queued for all outdated devices. Terminals will sync to the golden image (${goldenLabel}) on next check-in.`);
    setTimeout(() => {
      setAlertMessage(null);
    }, 5000);
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Alert element */}
      <AnimatePresence>
        {alertMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-semibold flex items-center gap-3 shadow-sm"
          >
            <span className="material-symbols-outlined text-emerald-600">verified</span>
            <span className="flex-1">{alertMessage}</span>
            <button onClick={() => setAlertMessage(null)} className="text-emerald-500 hover:text-emerald-700">
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top dashboard summary header matched with Enterprise style */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">App Version Analytics</h2>
          <p className="text-xs text-slate-500 mt-0.5">Real-time health monitoring of tablet hardware runs & build integrity.</p>
        </div>
        {stats.outdated > 0 && (
          <button
            onClick={handleUpdateAllOutdated}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-[#136bce] hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all active:scale-95 self-start lg:self-auto"
          >
            <span className="material-symbols-outlined text-[16px]">system_update_alt</span>
            Trigger Bulk OTA Updates
          </button>
        )}
      </div>

      {/* Stats Cards - Outdated vs Updated on Top */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Total Scanned Devices</span>
            <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600 border border-blue-100">
              <span className="material-symbols-outlined text-[18px]">devices</span>
            </div>
          </div>
          <h3 className="text-2xl font-black text-slate-900 mt-1">{stats.total} <span className="text-xs font-normal text-slate-400">devices</span></h3>
          <p className="text-[11px] text-slate-500">{selectedFactoryFilter}</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold text-red-500 tracking-wider">Outdated Versions</span>
            <div className="p-1.5 rounded-lg bg-red-50 text-red-600 border border-red-100">
              <span className="material-symbols-outlined text-[18px]">gpp_maybe</span>
            </div>
          </div>
          <h3 className="text-2xl font-black text-red-600 mt-1">{stats.outdated} <span className="text-xs font-normal text-red-400">devices</span></h3>
          <p className="text-[11px] text-red-500 font-semibold">{stats.percentageOutdated}% of pool requires update</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold text-emerald-500 tracking-wider font-semibold">Updated Versions</span>
            <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-100">
              <span className="material-symbols-outlined text-[18px]">gpp_good</span>
            </div>
          </div>
          <h3 className="text-2xl font-black text-emerald-600 mt-1">{stats.updated} <span className="text-xs font-normal text-emerald-400">devices</span></h3>
          <p className="text-[11px] text-emerald-500 font-semibold">{latestVersion ? `Build ${latestVersion} Golden standard` : 'Latest build is golden standard'}</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Device Compliance Rate</span>
            <div className="p-1.5 rounded-lg bg-amber-50 text-amber-600 border border-amber-100">
              <span className="material-symbols-outlined text-[18px]">security</span>
            </div>
          </div>
          <h3 className="text-2xl font-black text-slate-900 mt-1">{stats.total > 0 ? Math.round((stats.updated / stats.total) * 100) : 100}%</h3>
          <div className="w-full bg-slate-100 h-1.5 rounded-full mt-1.5 overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${stats.percentageOutdated > 30 ? 'bg-red-500' : 'bg-emerald-500'}`}
              style={{ width: `${stats.total > 0 ? (stats.updated / stats.total) * 100 : 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Main body of Version controls */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-6">
        <div className="flex flex-col gap-5 pb-5 border-b border-slate-100">
          <div className="flex flex-col gap-2.5">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1">
              <span className="material-symbols-outlined text-[14px]">filter_alt</span>
              Filter by Operational Node
            </span>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
              {factorySummary.map((facItem) => {
                const fac = facItem.name;
                const isSelected = selectedFactoryFilter === fac;
                
                // Assign a distinct icon for each node type
                let icon = "domain";
                if (fac.includes("Apex")) icon = "precision_manufacturing";
                else if (fac.includes("Paramount")) icon = "workspace_premium";
                else if (fac.includes("Zenith")) icon = "wb_sunny";
                else if (fac.includes("Southern")) icon = "toll";
                else if (fac.includes("Northern")) icon = "waves";
                else if (fac === "All Factories") icon = "lan";

                const displayName = fac === "All Factories" ? "All Factories" : fac.split(" (")[0];

                return (
                  <button
                    key={fac}
                    onClick={() => setSelectedFactoryFilter(fac)}
                    className={`flex flex-col justify-between p-3 rounded-xl border transition-all text-left hover:shadow-xs active:scale-98 group relative ${
                      isSelected
                        ? 'bg-blue-50/70 text-[#136bce] border-blue-200 ring-1 ring-blue-100 shadow-xs'
                        : 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`material-symbols-outlined text-[18px] transition-transform group-hover:scale-110 ${
                        isSelected ? 'text-[#136bce]' : 'text-slate-400'
                      }`}>
                        {icon}
                      </span>
                      <span className="font-bold text-xs truncate max-w-full leading-tight">{displayName}</span>
                    </div>
                    
                    <div className="flex items-center justify-between w-full mt-1">
                      <span className="text-[10px] text-slate-400 font-medium">
                        {facItem.total} device{facItem.total !== 1 ? 's' : ''}
                      </span>
                      {facItem.outdated > 0 ? (
                        <span className="inline-flex items-center gap-0.5 rounded-md bg-red-50 px-1.5 py-0.5 text-[9px] font-bold text-red-600 border border-red-100">
                          {facItem.outdated} legacy
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold text-emerald-600 border border-emerald-100">
                          OK
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
            <div className="text-xs text-slate-500 font-medium">
              Showing <span className="font-bold text-slate-800">{filteredUsers.length}</span> inspector record{filteredUsers.length !== 1 ? 's' : ''} out of <span className="font-bold text-slate-800">{stats.total}</span> under current filters
            </div>
            <div className="relative w-full sm:w-64">
              <input
                type="text"
                placeholder="Search inspector, email, build, OS..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-4 text-xs placeholder-slate-400 focus:border-[#136bce] focus:outline-none focus:ring-1 focus:ring-[#136bce]"
              />
              <span className="material-symbols-outlined absolute left-3 top-2.5 text-[16px] text-slate-400">search</span>
            </div>
          </div>
        </div>

        {/* Device Table - Outdated versions are sorted first */}
        <div className="overflow-x-auto border border-slate-100 rounded-xl">
          <table className="w-full text-left text-sm text-slate-600 min-w-[750px]">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase font-bold tracking-wider text-slate-500">
              <tr>
                <th className="px-6 py-4">User Name & Email</th>
                <th className="px-6 py-4">Factory</th>
                <th className="px-6 py-4">App Version</th>
                <th className="px-6 py-4">Android version</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-400">
                    <div className="h-7 w-7 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent mx-auto mb-3"></div>
                    <p className="text-xs font-medium">Loading device version data...</p>
                  </td>
                </tr>
              ) : loadError ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center">
                    <span className="material-symbols-outlined text-[36px] block text-red-300 mb-2">error</span>
                    <p className="text-xs font-semibold text-red-500">{loadError}</p>
                    <button onClick={fetchVersionLog} className="mt-2 text-xs text-[#136bce] underline">Retry</button>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-400">
                    <span className="material-symbols-outlined text-[36px] block text-slate-300 mb-2">find_in_page</span>
                    No inspectors matched for the active version filter parameters.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    className={`transition-colors ${user.isOutdated ? 'bg-red-50/20 hover:bg-red-50/40' : 'hover:bg-slate-50/50'}`}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-bold text-slate-900 leading-tight flex items-center gap-1.5">
                          {user.name}
                          {user.isOutdated && (
                            <span className="inline-flex h-2 w-2 rounded-full bg-red-500 animate-pulse" title="Requires immediate patch"></span>
                          )}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-semibold text-slate-700">{user.factoryName}</span>
                    </td>
                    <td className="px-6 py-4">
                      {user.appVersion ? (
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                            user.isOutdated
                              ? 'bg-red-50 text-red-700 border border-red-200'
                              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          }`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                            {user.appVersion}
                          </span>
                          {user.isOutdated && (
                            <span className="text-[10px] text-red-500 font-bold uppercase tracking-wider">Outdated</span>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-slate-50 text-slate-400 border border-slate-200">
                          Not reported
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-xs text-slate-800 bg-slate-50 border border-slate-100 px-2 py-1 rounded">
                        {user.androidVersion || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {user.isOutdated ? (
                        <button
                          onClick={() => handleTriggerMockOTA(user.name)}
                          className="px-2.5 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-bold transition-all inline-flex items-center gap-1"
                        >
                          <span className="material-symbols-outlined text-[13px]">install_mobile</span>
                          Push App Update
                        </button>
                      ) : user.appVersion ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold">
                          <span className="material-symbols-outlined text-[15px]">done</span>
                          Compliant
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-slate-400 text-xs font-semibold">
                          <span className="material-symbols-outlined text-[15px]">sync_disabled</span>
                          Awaiting sync
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
